/**
 * Aktuelle App-Version + Changelog. Manuell pflegen bei jedem Release.
 * Wird auf der About-Seite angezeigt.
 */
export const APP_VERSION = '0.15.0';

export interface ChangelogEntry {
  version: string;
  date: string; // YYYY-MM-DD
  notes: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.15.0',
    date: '2026-09-11',
    notes: [
      'Todos lassen sich einer Sektion zuweisen (z.B. „Haus 1“). Ohne Sektion gilt ein Todo als „Allgemein“. In der Todo-Card des Projekts filtern Chips nach Sektion, und neue Todos übernehmen die gerade gewählte Sektion.',
      'Todos verschieben: Im Bearbeiten-Modus können Projekt und Sektion geändert werden. Nach dem Verschieben in ein anderes Projekt lässt sich das über „Rückgängig“ zurücknehmen.',
      'Todo-Übersicht mit Filtern: Suche in Titel und Beschreibung, Status (offen / erledigt / beides), Projekt, Sektion, „Erstellt von“ (inkl. „Von mir erstellt“), Projektleiter und Bauleiter. Die Filter bleiben beim Neuladen erhalten und lassen sich als Link teilen. Auf dem Handy sind die Zusatzfilter eingeklappt.',
      'Aufgaben auf dem Handy umsortieren: Knopf „Sortieren“ in der Sektion zeigt eine kompakte Liste mit ↑/↓ pro Aufgabe.',
      'Vorlagen duplizieren: Knopf „Duplizieren“ im Vorlagen-Editor öffnet eine Kopie mit allen Positionen, die unter neuem Namen gespeichert werden kann.',
    ],
  },
  {
    version: '0.14.5',
    date: '2026-06-09',
    notes: [
      'Foto-Lightbox respektiert das Seitenverhältnis: Bilder werden jetzt mit object-fit: contain dargestellt statt cover. Hochkant-Fotos werden nicht mehr links/rechts beschnitten, Querformat nicht oben/unten. AuthedImage hat ein neues fit-Prop (cover für Grid-Thumbnails, contain für Lightbox).',
    ],
  },
  {
    version: '0.14.4',
    date: '2026-06-09',
    notes: [
      'Foto-Upload zum dritten und letzten Mal Orientation: kompletter Rewrite. createImageBitmap auf iOS Safari macht zu viel/zu wenig Rotation, der manuelle EXIF-Transform aus 0.14.3 hat doppelt gedreht. Jetzt: HTMLImageElement laden → naturalWidth/naturalHeight nutzen → direkt aufs Canvas. Alle modernen Browser inkl. iOS 13.4+ respektieren EXIF im <img>-Element automatisch, kein manueller Transform mehr nötig. Foto kommt in der Orientation an in der es geschossen wurde, ohne Aspect-Ratio-Verzerrung.',
    ],
  },
  {
    version: '0.14.3',
    date: '2026-06-09',
    notes: [
      'Foto-Upload Bugfix: Hochkant-Fotos vom iPhone wurden auf Querformat zugeschnitten. Ursache war ein bekannter iOS-Safari-Bug mit createImageBitmap({ imageOrientation: "from-image" }) — EXIF-Orientation wurde nicht zuverlässig angewandt. Wir lesen die Orientation jetzt selbst aus dem JPEG-Header und rotieren über eine Canvas-Transform-Matrix. Funktioniert deterministisch auf allen Browsern.',
    ],
  },
  {
    version: '0.14.2',
    date: '2026-06-09',
    notes: [
      'Foto-Lightbox: Schliessen-Button und Pfeile respektieren jetzt iOS Safe-Area (Dynamic Island, Notch, Home-Indicator, Landscape-Kerben). Auf iPhone war der × bisher unter der Statusbar/Uhr verdeckt.',
    ],
  },
  {
    version: '0.14.1',
    date: '2026-06-09',
    notes: [
      'Fotodokumentation Bugfix: Bilder wurden nicht angezeigt. Helmet-CSP blockte die blob:-URLs aus URL.createObjectURL() (wir laden Fotos authentifiziert als Blob). img-src um "blob:" ergänzt.',
      'Fotodokumentation Upload-Flow: Beim Upload öffnet jetzt zuerst der Datei-/Kamera-Picker, danach ein Modal mit Kategorie-Auswahl (inkl. "Neue Kategorie anlegen") und optionaler Beschriftung. Verhindert versehentliches Ablegen im falschen Topf.',
      'Fotodokumentation: Fotos können nachträglich in eine andere Kategorie verschoben werden — Dropdown im Lightbox-Footer. Änderung wird im Audit-Log dokumentiert.',
    ],
  },
  {
    version: '0.14.0',
    date: '2026-06-09',
    notes: [
      'Neues Modul Fotodokumentation: pro Projekt frei definierbare Kategorien ("Einlagen Decke UG", "Verteiler EG", …) mit Foto-Upload vom Smartphone oder Kamera. Galerie im Projekt-Detail unter "Fotos" und globale Übersicht via neuem Sidebar-Punkt "Fotodokumentation".',
      'Foto-Upload mit Client-Kompression (max 2560 px lange Kante, JPEG Q=85) — Smartphone-Fotos schrumpfen vor dem Upload auf ~700–1200 KB, EXIF-Orientation wird respektiert. Mehrfach-Upload unterstützt; Kamera-Knopf öffnet auf Mobile direkt die Kamera-App.',
      'Lightbox-Anzeige mit Pfeil-Navigation und Esc-zu-schliessen. Fotos werden lazy geladen, bleiben aber via Bearer-Token authentifiziert (kein Public-Endpoint).',
      'Berechtigungen: Upload und Kategorie-Anlage für alle Rollen inkl. Mitarbeiter. Löschen: PL/Admin jederzeit, eigene Fotos zusätzlich bis 24 h nach Upload (Fehlupload-Korrektur).',
    ],
  },
  {
    version: '0.13.0',
    date: '2026-06-09',
    notes: [
      'Projekt-Stammdaten: neues Freitext-Feld "Beschreibung" für Türcodes, Telefonnummern von Key-Personen, Schlüssel-Standorte usw. Wird in der Projekt-Detailansicht oberhalb der Bemerkungen angezeigt und im Projekt-Formular oberhalb der "Bemerkungen / Nächste Schritte" gepflegt. Bestehende notes-Spalte bleibt für kurze, aktuelle Hinweise.',
      'TaskEditModal (Mobile-View): Spalte "Erledigt am" entfernt — gibt es in keinem anderen View und wird nicht benötigt.',
      'TaskEditModal: Datums-Felder haben jetzt einen "×"-Knopf zum Entfernen. iOS Safari und viele Mobile-Browser liessen sich vorher nach einer einmaligen Datumssetzung nicht mehr leer setzen.',
      'Berechtigungen: Bauleiter dürfen jetzt im Projekt-Detail Aufgaben und Gruppen-Header anlegen ("+ Aufgabe", "+ Gruppe", "+ Aufgabe"-Knopf an Gruppen-Headern). Section-Anlage, Section-Rename und Task-Löschung bleiben Admin/PM-only.',
    ],
  },
  {
    version: '0.12.1',
    date: '2026-06-09',
    notes: [
      'Bugfix Passwort-Wechsel: nach einem vom Admin erzwungenen Reset konnte der User sein neues Passwort nicht setzen. Bei falscher Eingabe des aktuellen (admin-gesetzten) Passworts schickte das Backend HTTP 401, der globale Axios-Interceptor loggte den User aus und das Force-Change-Modal verschwand — der User landete wieder auf dem Login und das alte Passwort blieb gültig. Backend liefert jetzt 400 bei falschem Bestätigungs-Passwort; Interceptor whitelistet /auth/change-password zusätzlich als Sicherheitsnetz.',
    ],
  },
  {
    version: '0.12.0',
    date: '2026-05-28',
    notes: [
      'Terminprogramm: Projekt-Balken (Start/Ende aus den Projekt-Stammdaten) wird wieder in der Projekt-Header-Zeile angezeigt. Klick auf den Balken oder den Projekt-Titel öffnet das Projekt-Detail.',
      'Terminprogramm: Detail-Sektion-Zeilen sind deutlich kompakter (Höhe 50 → 30 px, Balkenhöhe 16 → 10 px). Mehr Sektionen ohne Scrollen sichtbar.',
      'Terminprogramm: Sektion-Titel ist jetzt ein Link — Klick öffnet das Projekt-Detail mit der entsprechenden Sektion vorausgewählt (analog zum Projekt-Titel).',
    ],
  },
  {
    version: '0.11.6',
    date: '2026-05-28',
    notes: [
      'CRM-Sidecar install-service.bat zeigt jetzt auf real-Python statt PyManager-Shim. Der PyManager-Stub funktioniert nicht im Service-Kontext (LocalSystem hat keine PyManager-Runtimes), Service kam in SERVICE_PAUSED. Echter Pfad auf Hauri-Server: C:\\Program Files\\Python\\python.exe.',
    ],
  },
  {
    version: '0.11.5',
    date: '2026-05-27',
    notes: [
      'CRM-Sidecar Setup auf .bat statt PowerShell umgestellt: install-service.bat / uninstall-service.bat sind stumpfe Batch-Files mit den NSSM-Befehlen — keine Parser-Quirks, keine Encoding-Fallen, keine NativeCommandError-Falle. Pfade als Variablen oben in der Datei, anpassbar wenn nötig.',
    ],
  },
  {
    version: '0.11.4',
    date: '2026-05-27',
    notes: [
      'CRM-Sidecar lädt automatisch server/.env (stdlib-only Parser, keine dotenv-Dependency). Sidecar sieht jetzt dieselben Credentials wie der Node-Server — kein manuelles Setzen von Shell-Variablen mehr nötig.',
    ],
  },
  {
    version: '0.11.3',
    date: '2026-05-27',
    notes: [
      'CRM-Sidecar setup-windows.ps1 robuster gegen stderr-Output nativer Commands: pip + nssm schreiben harmlose Warnungen nach stderr, das hat zusammen mit globalem ErrorActionPreference="Stop" + Pipeline-Verarbeitung NativeCommandError ausgelöst. Native Aufrufe gehen jetzt durch Invoke-Native, das stdout+stderr direkt in eine Variable capturet ohne Pipeline und nur den Exit-Code prüft. pyodbc-Check vor Install (PyManager etc. haben es oft schon dabei). PyManager-Pfad in der Python-Suche ergänzt.',
    ],
  },
  {
    version: '0.11.2',
    date: '2026-05-27',
    notes: [
      'CRM-Sidecar Setup-Scripts ASCII-only — PowerShell 5.x auf Windows-Servern hatte Mühe mit UTF-8 ohne BOM (em-dashes wurden zu Multi-Byte-Zeichen, kaputte String-Grenzen führten zu Parser-Errors über eckigen Klammern in Regex). Setup-Scripts laufen jetzt auf jedem Windows ohne Encoding-Risiko.',
    ],
  },
  {
    version: '0.11.1',
    date: '2026-05-27',
    notes: [
      'CRM-Sidecar Auto-Setup: ein einziges PowerShell-Script setup-windows.ps1 macht alles — Python finden, pyodbc installieren, Test-Run, NSSM-Service registrieren mit Logs + Auto-Restart, Health-Check. Bei Fehlern wird sofort der echte Crash-Grund ausgegeben statt nur "SERVICE_STOPPED". Idempotent — überschreibt bestehende Installation sauber.',
      'uninstall-windows.ps1 zum sauberen Entfernen.',
      'NSSM-Service kommt jetzt mit konfigurierten Logs (stdout+stderr in server/logs/), Auto-Restart bei Crash und Log-Rotation (1 MB). Bei Problemen ist der Crash-Grund sofort in crm-sidecar.stderr.log lesbar.',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-05-27',
    notes: [
      'CRM-Encoding sauber gelöst über Python-Sidecar: kleiner HTTP-Adapter (server/crm-sidecar/main.py, ~250 LOC, single file mit nur pyodbc als Dependency, kein Flask/Bottle), der die Braso-Calls macht und über pyodbc.setdecoding(SQL_CHAR, cp1252) korrekt UTF-8-encodierte Strings liefert — Umlaute kommen jetzt richtig durch (Müller, Würth, Bühler, Schöll, etc.). Setup-Anleitung in server/crm-sidecar/README.md mit NSSM-Service-Konfiguration.',
      'Stromo Node-Server entscheidet beim Start: ist CRM_BRASO_SIDECAR_URL gesetzt, gehen alle Calls per fetch() über den Sidecar — node-odbc wird gar nicht erst geladen. Ohne Sidecar bleibt der bisherige direkte ODBC-Pfad als Fallback (mit dem Replacement-Character-Problem).',
      'Optionales Shared-Secret CRM_SIDECAR_TOKEN für Defense in Depth, falls der Sidecar nicht nur an 127.0.0.1 gebunden bleibt.',
    ],
  },
  {
    version: '0.10.6',
    date: '2026-05-27',
    notes: [
      'CRM-Diagnose erweitert um Encoding-Probe: /crm/diagnose probiert jetzt @HEX(), HEX(), CAST AS LONG VARBINARY, CAST AS BINARY, @RAW() und plain SELECT durch und liefert für jede Strategie zurück, ob sie geht und welche Property-Typen rauskommen. Damit lässt sich auf dem konkreten Braso-System feststellen, welche SQL-Funktion uns Roh-Bytes liefert — Grundlage für die korrekte Umlaut-Behandlung.',
      'iconv-lite (pure-JS) als Dependency — kein nativer Compile, kein Build-Tool nötig.',
    ],
  },
  {
    version: '0.10.5',
    date: '2026-05-27',
    notes: [
      'CRM-Suche Umlaut-Workaround: der Gupta-ODBC-Driver liefert Strings in Windows-1252-Bytes zurück, node-odbc interpretiert sie aber als UTF-8 — Umlaute (ä/ö/ü/ß) erscheinen als „�" (Replacement-Character). Eine echte Reparatur auf JS-Seite ist nicht mehr möglich (Bytes sind weg), wir umgehen das beim Such-Pattern: Umlaute im Sucheingabe werden zusätzlich als SQL-Single-Char-Wildcard "_" mitgesendet, sodass „Müller" auch eine DB-Zeile mit „M�ller" findet.',
      'Import-Modal zeigt einen gelben Hinweis-Banner wenn die Treffer „�" enthalten — User weiss dann, dass er beim Speichern den Namen manuell korrigieren soll.',
      'Saubere Encoding-Lösung erfordert einen node-odbc-Rebuild mit UNICODE-Define — siehe Doku.',
    ],
  },
  {
    version: '0.10.4',
    date: '2026-05-27',
    notes: [
      'CRM-Suche liefert wieder Treffer: der Gupta-ODBC-Driver liefert Spalten in Result-Rows mit Tabellen-Präfix (z.B. "ADRESSE.NAME1") zurück, daher matchte das Row-Mapping vorher nichts und alle Treffer verschwanden in der Liste. Stromo nutzt jetzt explizite AS-Aliase in der SELECT-Klausel, sodass die Property-Keys ohne Präfix durchkommen.',
    ],
  },
  {
    version: '0.10.3',
    date: '2026-05-27',
    notes: [
      'CRM-Suche robuster gegen ODBC-Spalten-Casing: cleanRow() greift jetzt case-insensitiv auf die Row-Properties zu. Hintergrund — der Gupta-ODBC-Driver liefert je nach Konfiguration uppercase ODER lowercase Property-Keys; vorher fand der Mapper "NAME1" nur wenn der Driver ebenfalls so liefert, sonst kamen Treffer aus der DB zwar zurück, wurden im Mapping aber zu leeren Kandidaten und damit nicht in der Trefferliste angezeigt.',
      'Neuer Admin-Endpoint GET /api/v1/crm/diagnose: liefert COUNT(*) der ADRESSE-Tabelle, die rohen Spaltennamen wie ODBC sie meldet, und die ersten 3 Rows als raw + parsed. Damit lässt sich „findet keine bekannten Namen"-Probleme schnell isolieren.',
    ],
  },
  {
    version: '0.10.2',
    date: '2026-05-27',
    notes: [
      'CRM-Suche: Inline-SQL statt parametrisierter Queries — der Gupta-SQLBase-ODBC-Driver unterstützt SQLDescribeParam nicht, daher scheiterte jeder mit „?"-Platzhalter formulierte Aufruf am Fehler „[odbc] Error getting information about parameters". Stromo baut die Statements jetzt inline mit sqlString()-Escape (Single-Quote-Verdopplung) und sqlInt()-Validation, sodass die Suche zuverlässig durchläuft.',
    ],
  },
  {
    version: '0.10.1',
    date: '2026-05-27',
    notes: [
      'CRM-Suche: UPPER() raus, da im SQLBase-ODBC-Driver nicht zuverlässig vorhanden. Statt SQL-seitiger Case-Folding generiert Stromo jetzt mehrere LIKE-Patterns (Original, lowercase, uppercase, Capitalize) und ORed sie über NAME1/NAME2/NAME/VORNAME. Treffer-Quote in den meisten Fällen identisch, Kompatibilität deutlich besser.',
      'CRM-Fehler werden jetzt im UI sichtbar statt als „Interner Serverfehler" zu verstummen: jeder ODBC-Call ist in wrapOdbc() eingewickelt und liefert HTTP 502 mit dem konkreten ODBC-Error im Response — der CrmImportModal zeigt die Meldung direkt unter dem Suchfeld an. Stack-Traces landen weiterhin im Log.',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-05-27',
    notes: [
      'CRM-Integration für Kontakt-Import (erstes Beispiel: Braso/Xpandit via Gupta SQLBase ODBC). Aktivierung über env CRM_ENABLED=true + CRM_TYPE=braso — ohne diese Flags bleibt das Feature komplett stumm und der UI-Knopf erscheint nicht.',
      'Migration 010 ergänzt contacts.external_id + contacts.external_source mit Unique-Index — Bereits-importiert-Erkennung im Such-Modal, später Re-Sync möglich.',
      'Neue Server-Routen: GET /crm/status (alle eingeloggten User), GET /crm/search?q= und GET /crm/candidate/:id (admin/PL). Such-Strategie: bei Telefon-Eingabe (mit oder ohne Format) → ADR_TEL exakt + Fallback LIKE auf letzte Ziffern; sonst Namens-Suche per LIKE über NAME1/NAME2/NAME/VORNAME.',
      'Frontend: CrmImportModal in der Kontakte-Seite — Suche mit 300-ms-Debounce, Trefferliste zeigt Privatperson/Firma + E-Mail/Telefon/Adresse, Klick präselektiert das normale Kontakt-Formular mit external_id+source. Bereits-importierte Kontakte sind in der Liste markiert und nicht klickbar.',
      'odbc als optionalDependency — auf Systemen ohne ODBC-Driver (z.B. Mac-Dev) wird das Modul still übersprungen, /crm/status liefert ready=false + Fehlertext, und der UI-Knopf bleibt disabled.',
    ],
  },
  {
    version: '0.9.1',
    date: '2026-05-27',
    notes: [
      'Scheduler-Diagnose: beim Start loggt der Server jetzt zusätzlich zu "eingeplant" den konkreten nächsten Lauf-Zeitpunkt (nextRun) für Verzugs-Notifier und DB-Backup. Damit ist auf einen Blick prüfbar, ob die Cron-Ausdrücke korrekt geparst werden und ob die richtige Zeitzone (Europe/Zurich) greift.',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-05-27',
    notes: [
      'Gantt komplett umgebaut: pro Projekt werden jetzt alle Sektionen (Haus 1, Haus 2 …) als eigene Zeilen getrennt dargestellt. Pro Aufgabe gibt es zwei gestapelte Balken in derselben Zeile — oben Bauzeit (schedule_start/end) immer in Stromo-Blau, unten Unser-Termin (internal_start/end) in der Status-Farbe der Aufgabe (Verzug = rot, identisch zur Ampel-Logik der Detail-Tabelle).',
      'Der bisherige breite Projekt-Header-Balken mit Auftragsnummer ist entfallen — Projektzeile zeigt nur noch Titel/Auftragsnummer/PL/BL links, die rechte Spalte gehört den Sektionen darunter. Heute-Linie läuft durch alle Zeilen durch.',
      'Backend: GET /gantt liefert jetzt projects → sections → tasks (geschachtelt). Sektion-Sortierung übernommen aus project_sections.sort_order.',
    ],
  },
  {
    version: '0.8.4',
    date: '2026-05-27',
    notes: [
      'Doku auf Stand 0.8.x gebracht: docs/html/index.html, deployment.html, troubleshooting.html, architecture.html, user-guide.html und development.html dokumentieren jetzt durchgehend Force-Password-Change, Login-Rate-Limit, Active-User-Check, JWT-Secret-Mindestlänge, Upload-Magic-Bytes, Letzter-Admin-Schutz, Log-Rotation, stündliches Backup, manueller Backup-Endpoint und schnelle Schweizer Datumseingabe mit Tab-Navigation.',
      'Neue Sections "Security-Konzept" und "Operatives" in architecture.html. Neuer Abschnitt "Login/Passwort/Rate-Limit" und "Logs & Backup" in troubleshooting.html. Migration 009 in der Migrationsliste in deployment.html. Upgrade-Hinweis (JWT-Secret 32 Zeichen) ergänzt.',
    ],
  },
  {
    version: '0.8.3',
    date: '2026-05-27',
    notes: [
      'Tägliche Log-Rotation: Pino schreibt jetzt parallel zu stdout auch in LOG_DIR/stromo-YYYY-MM-DD.log. Bei Datumswechsel wird automatisch eine neue Datei angelegt, ältere als LOG_RETENTION_DAYS (Default 30) Tage werden gelöscht. Eigene DailyRotatingFileStream-Klasse — keine zusätzliche Dependency.',
      'DB-Backup stündlich statt täglich. Job läuft jetzt intern im Stromo-Prozess (über node-cron) — keine externe PM2/Task-Scheduler-Kette mehr nötig. Konsistente Snapshots via VACUUM INTO, Retention 24 Stück (= 1 Tag bei stündlich, via BACKUP_RETENTION konfigurierbar).',
      'Neuer Admin-Endpoint POST /admin/backup/run für manuelle Backups vor Updates (liefert Pfad + Grösse + Dauer + Retention-Stats).',
      'Doku (deployment.html) entsprechend aktualisiert; .env.example um BACKUP_RETENTION, LOG_DIR, LOG_RETENTION_DAYS ergänzt.',
    ],
  },
  {
    version: '0.8.2',
    date: '2026-05-27',
    notes: [
      'Schnelle Datumseingabe in der Detail-Tabelle: Datums-Zellen akzeptieren jetzt Schweizer Kurzformat — "1.1.26", "01.01.2026", "010126" oder "01012026" werden alle zum gleichen Datum geparst. Anzeige weiterhin "DD.MM.YYYY".',
      'Tab-Navigation über die vier Datums-Felder einer Aufgabenzeile: Datums-Zellen sind jetzt fokussierbar, Fokus öffnet automatisch den Edit-Modus, Tab/Enter committen und springen zum nächsten Datum. Esc verwirft die Eingabe.',
      'Ungültige Datums-Eingabe (z.B. "31.02") wird mit rotem Rand markiert; Tab bricht den Sprung ab, damit nichts fälschlich gespeichert wird.',
    ],
  },
  {
    version: '0.8.1',
    date: '2026-05-27',
    notes: [
      'Sicherheits-Härtung Teil 2 (Low/Info-Items aus dem Review):',
      'L8 — Force-Password-Change: Beim Erstinstall und nach Admin-gesetzten Passwörtern wird der Anwender beim ersten Login zwingend zum Passwort-Wechsel geleitet (Migration 009 ergänzt password_change_required; neuer Endpoint POST /auth/change-password; im Frontend ein Modal das die App bis zur Änderung sperrt).',
      'L1 — JWT_SECRET-Minimum auf 32 Zeichen erhöht (vorher 16). Bestehende kürzere Secrets müssen vor Server-Start ersetzt werden.',
      'L2 — bcrypt-Cost-Faktor von 10 auf 12 erhöht. Login dauert nun ~250 ms (gewollt), brute-force-Kosten vervierfacht. Zentrale BCRYPT_ROUNDS-Konstante.',
      'L3 — cookie-parser-Middleware entfernt (toter Code, kein Use-Case).',
      'M8 — escapeHtml in delayNotifier escapt jetzt auch " und \', konsistent mit den PDF-Templates.',
      'B1 — pino-http loggt 401/403/429 jetzt als info, andere 4xx als warn — nicht mehr als error. Kein Log-Spam mehr bei abgelaufenen Tokens oder Rate-Limit-Hits.',
    ],
  },
  {
    version: '0.8.0',
    date: '2026-05-27',
    notes: [
      'Sicherheits-Härtung nach vollständigem Code-Review (siehe Doku/Architektur):',
      'H1 — Pino-Logger redacted ab jetzt Authorization-Header, Cookies, password/token-Felder. JWT-Tokens landen nicht mehr in Klartext in den Logfiles.',
      'H2 — Login-Rate-Limit (10 Versuche/15 min pro IP). Schützt vor Brute-Force gegen schwache Passwörter und vor DoS via bcrypt-Synchron-CPU.',
      'H3 — requireAuth prüft ab jetzt pro Request ob der User noch aktiv ist (1 SELECT in users). Deaktivierte/gefeuerte User verlieren sofort den API-Zugriff, statt 30 Tage lang weiter zu wirken. Rolle wird ebenfalls frisch aus DB gelesen.',
      'H4 — Upload-Validierung: zusätzlich zu MIME-Hint (Client-meldet) jetzt Extension-Whitelist + Magic-Bytes-Verifikation auf der gespeicherten Datei. application/octet-stream-Catchall raus. Bei Fehler wird die Datei vom Disk geräumt.',
      'M1 — jwt.verify mit explizitem algorithms=[HS256], schützt vor Algorithm-Confusion.',
      'M2 — restoreTodo: Owner-/Role-Check ergänzt. Mitarbeiter kann fremde, von Admin/PM gelöschte Todos nicht mehr wiederherstellen.',
      'M3 — Backup-Skript nutzt jetzt SQLite VACUUM INTO statt fs.copyFile — konsistente Snapshots auch bei aktivem WAL-Writer.',
      'M4 — Letzter-aktiver-Admin-Schutz: Deaktivieren oder Umrollieren wird mit 409 abgelehnt, wenn dadurch kein aktiver Admin mehr übrig wäre.',
      'M5 — PDF-Concurrency-Cap (max 3 parallele Renders). Verhindert OOM bei vielen gleichzeitigen Exports.',
      'M6 — Login-Timing konstant: bei nicht existentem User wird trotzdem ein Dummy-bcrypt-Vergleich ausgeführt, damit Antwortzeit nicht User-Existenz verrät.',
      'M7 — node-cron auf 4.2.1 hochgezogen (alte 3.x hatte transitive uuid-CVE). API-Aufruf-Stelle (stop async) angepasst.',
      'B3 — Doku: DELAY_CRON → DELAY_NOTIFY_CRON (konsistent mit Code und .env.example).',
      'Zusatz: trust proxy aktiviert, damit Rate-Limit hinter nginx pro Client-IP greift.',
    ],
  },
  {
    version: '0.7.1',
    date: '2026-05-27',
    notes: [
      'Komplette Doku-Migration von Markdown nach HTML: docs/html/ enthält jetzt ein eigenständiges Doku-Set (Übersicht, Bedienung, Deployment, Troubleshooting, Architektur, Entwicklung) mit gemeinsamer Sidebar-Navigation, mobile-fähig, druckbar, offline lesbar. Inhalte wurden auf Stand 0.7.0 gebracht (neue Rolle Mitarbeiter, Todos-Modul, Soft-Delete, Auth-Persistenz, Safe-Area, alle Migrationen bis 008).',
      'README.md verweist nun auf docs/html/index.html als Einstieg.',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-05-27',
    notes: [
      'Auth-Persistenz auf iOS-PWA: JWT-Token wird jetzt in localStorage abgelegt statt sessionStorage. Damit überlebt der Login das Schliessen / Neu-Öffnen der App. JWT-Default-Lebensdauer von 12h auf 30d hochgesetzt (Mobile-UX). Bei 401 redirected der Interceptor unverändert zum Login.',
      'Toast-Undo bei Lösch-Aktionen: nach jedem DELETE auf Aufgabe / Sektion / Todo erscheint unten eine Snackbar "X gelöscht" mit Rückgängig-Knopf (7 Sekunden lang). Server-seitig läuft das über Soft-Delete: Migration 008 ergänzt deleted_at-Spalten + Filter-Indizes auf tasks/project_sections/todos. Listings filtern automatisch auf deleted_at IS NULL. Neue Endpoints POST /tasks/:id/restore, /sections/:id/restore, /todos/:id/restore.',
      'Gantt-, Verzugs-Mail- und Template-Merge-Queries respektieren ebenfalls deleted_at — soft-gelöschte Tasks/Sektionen tauchen dort nicht mehr auf.',
    ],
  },
  {
    version: '0.6.3',
    date: '2026-05-27',
    notes: [
      'iOS-PWA Safe-Area-Fix: Burger-Menü, Topbar und Content-Bereich respektieren jetzt env(safe-area-inset-*). Dynamic Island / Statusleiste oben und Home-Indicator unten überdecken die UI nicht mehr. Topbar erhält dynamische min-height (Basis-Höhe + Top-Inset), Burger-Button rutscht entsprechend nach unten. Sidebar-Drawer hat innen oben/unten Safe-Area-Padding. Links/rechts wird auch der Landscape-Notch berücksichtigt.',
    ],
  },
  {
    version: '0.6.2',
    date: '2026-05-27',
    notes: [
      'Todo-Übersicht erlaubt jetzt eine optionale Beschreibung — sowohl beim Anlegen (Quick-Add hat Titel-Input + Textarea) als auch beim Inline-Bearbeiten (Doppelklick auf Titel öffnet jetzt Titel + Beschreibung mit Speichern/Abbrechen). Verhalten ist nun symmetrisch zur Detail-Card.',
      'Quick-Add in der Todo-Übersicht: Cmd/Ctrl+Enter sendet ab (statt nacktem Enter — sonst kommt man nicht zur Beschreibung).',
    ],
  },
  {
    version: '0.6.1',
    date: '2026-05-27',
    notes: [
      'Hotfix Todos: POST /projects/:id/todos lieferte 500, weil die audit_log.entity_type-CHECK-Constraint "todo" nicht kannte. Migration 007 baut audit_log um (neue Tabelle mit erweitertem Constraint, Daten umkopieren, alte droppen, Indizes wiederherstellen).',
      'Hotfix Mitarbeiter-User: users.role-CHECK-Constraint kannte "employee" nicht — Mitarbeiter-User liessen sich gar nicht erst anlegen. Migration 007 baut users mit gleichem 12-Step-Verfahren um (defer_foreign_keys, damit FKs aus anderen Tabellen auf users(id) bestehen bleiben), updated_at-Trigger wird neu angelegt.',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-05-27',
    notes: [
      'Neues Modul Todos: pro Projekt anlegbare kleine Aufgaben/Notizen, unabhängig von der Detail-Aufgaben-Struktur. Mit Erledigt-Häkchen, Ersteller- und Abschluss-Info (nachvollziehbar wer wann was). Migration 006 legt todos-Tabelle an.',
      'Neuer Navigationspunkt "Todos" — Übersicht aller aktiven Projekte mit deren Todos darunter. Schnell-Erfassen pro Projekt direkt aus der Übersicht. Toggle für "Auch abgeschlossene anzeigen".',
      'Neue Card "Todos" in der Projekt-Detailansicht unter der Aufgaben-Tabelle — Erstellen, Bearbeiten, Abschliessen, Löschen.',
      'Neue Rolle Mitarbeiter (employee): kann Projekt-Übersicht, Terminprogramm und Projekt-Detail SEHEN aber nicht editieren. Kann Todos pro Projekt anlegen, eigene noch offene löschen, alle abschliessen. Aufgaben-Tabelle und Edit-Modal sind im Mitarbeiter-Modus read-only. Stammdaten (Vorlagen, Kontakte, Benutzer) und Audit-Log sind ausgeblendet.',
      'Berechtigungen aufgeräumt: ProjectsPage zeigt "Bearbeiten/Archivieren" nur noch wenn Server-Guard greift (admin + PL), Bauleiter und Mitarbeiter sehen die Buttons nicht mehr.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-05-27',
    notes: [
      'Finanzzahlen aus Stromo entfernt: Auftragsvolumen und Fakturiert sind aus Projekt-Formular, Header, Übersichts-Tabelle und PDF-Exports raus. DB-Migration 005 droppt die Spalten — bestehende Werte gehen verloren (so vom Kunden gewünscht, Finanzen werden ausserhalb gepflegt).',
      'Status-Farben deutlich besser unterscheidbar (Ampel-Logik): Offen=grau, Planung=amber/gelb, In Bearbeitung=blau, Verzug=rot, Abgeschlossen=grün. Vorher waren Offen/Planung/In-Bearbeitung alle in Blau-Tönen — kaum auf einen Blick differenzierbar. Greift überall: Select-Badges, Listen-Strip, Detail-Header, Gantt-Balken, PDF-Badges, Mobile-Card-Liste.',
      'Übersichts-PDF zeigt statt "Volumen gesamt/Offen" jetzt "Abgeschlossen/Archiviert" als Status-Statistik.',
    ],
  },
  {
    version: '0.4.2',
    date: '2026-05-27',
    notes: [
      '"+ Aufgabe"-Knopf bei Gruppen in der Projekt-Detail-Tabelle sitzt jetzt direkt neben dem Gruppen-Titel (statt in der rechten Action-Spalte, wo er vom Uppercase-Styling der Gruppe optisch verschluckt wurde). Analog zum Vorlagen-Editor.',
    ],
  },
  {
    version: '0.4.1',
    date: '2026-05-27',
    notes: [
      'Projekt-Detail (Desktop): Aufgaben lassen sich jetzt per Drag-and-Drop verschieben — wie im Vorlagen-Editor. Drag-Handle (⋮⋮) ganz links in der Tabelle, optimistisches Update der Reihenfolge, Server-Reorder läuft im Hintergrund',
      '"+ Aufgabe" direkt unter einer Gruppe: in der Aufgaben-Tabelle und im Vorlagen-Editor zeigt jeder Gruppen-Header jetzt einen "+ Position"-/"+ Aufgabe"-Button. Die neue Position wird ans Ende der Gruppe eingefügt (vor dem nächsten Gruppen-Header) — kein manuelles Verschieben mehr nötig',
      'Server: POST /sections/:id/tasks nimmt optional `after_task_id` an. Service-Layer schiebt nachfolgende Tasks in einer Transaktion um +1 nach unten und fügt die neue Task an exakt der gewünschten Position ein',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-05-27',
    notes: [
      'Neue Sektion anlegen: statt Browser-Prompt jetzt ein Modal mit drei Modi — (A) leere Sektion, (B) bestehende Sektion duplizieren (übernimmt Aufgaben-Struktur, Termine/Status bleiben leer), (C) aus aktiver Vorlage importieren',
      'Bugfix Vorlagen-Editor: Textfelder verloren nach jedem Tastendruck den Focus, weil der React-key den Position-Titel enthielt. Position-Drafts bekommen jetzt eine stabile UID — Eingabe läuft durchgehend',
    ],
  },
  {
    version: '0.3.11',
    date: '2026-05-27',
    notes: [
      'Vollständige Repo-Dokumentation aufgebaut: docs/architecture.md (Tech-Stack, Modul-Map, Datenmodell, API-Konventionen, Rollen-Matrix), docs/deployment.md (Windows-Server-Install Schritt-für-Schritt, NSSM, alle .env-Variablen), docs/troubleshooting.md (PDF/Chrome, Service-Probleme, PWA-Cache, E-Mail), docs/user-guide.md (Endanwender-Handbuch Desktop+Mobile), docs/development.md (Dev-Setup, Code-Konventionen, Release-Workflow)',
      'Root-README als Einstiegspunkt mit Quick-Start für Entwickler und Produktion und Übersicht aller Dokumentations-Dateien',
    ],
  },
  {
    version: '0.3.10',
    date: '2026-05-27',
    notes: [
      'Drei zusammenhängende PDF-Bugs endgültig gefixt: (1) PUPPETEER_CACHE_DIR wird jetzt in einer bootstrap.ts gesetzt, BEVOR irgendein anderes Modul geladen wird — vorher war es zu spät, Puppeteer schaute im LocalSystem-Default-Cache',
      '(2) Install-Routine lädt jetzt EXAKT die von Puppeteer geforderte Chrome-Version (z.B. 148.0.7778.97) statt der neuesten stable — sonst lehnt Puppeteer das frisch installierte Chrome ab',
      '(3) Globaler unhandledRejection- und uncaughtException-Handler verhindert dass der Service bei jedem fehlgeschlagenen PDF komplett crasht und von NSSM neu gestartet werden muss',
    ],
  },
  {
    version: '0.3.9',
    date: '2026-05-27',
    notes: [
      'Chrome-Install-Endpoint liefert bei Fehler den echten Error-Namen und -Message statt der generischen "Interner Serverfehler"-Meldung — die Diagnose-Card zeigt jetzt direkt was klemmt',
      'Stack-Trace im Fehlerfall als ausklappbares Detail in der Diagnose-Card',
    ],
  },
  {
    version: '0.3.8',
    date: '2026-05-26',
    notes: [
      'PWA-Cache-Bug behoben: HTML wird nicht mehr precached, sondern bei jeder Navigation frisch vom Server geholt (NetworkFirst). Nach Updates erscheint die neue Version sofort ohne manuelles Cache-Leeren.',
      'Auto-Versions-Check beim App-Start: vergleicht Frontend-Build mit /health-Server-Version, bei Mismatch wird Service Worker automatisch entregistriert + Caches geleert + Reload',
      '/api/v1/health liefert jetzt eine version mit',
      'Workbox: skipWaiting + clientsClaim + cleanupOutdatedCaches aktiviert',
    ],
  },
  {
    version: '0.3.7',
    date: '2026-05-26',
    notes: [
      'Chrome-Installation direkt aus der UI: neuer "Chrome jetzt installieren"-Knopf in der PDF-Diagnose lädt Chrome via @puppeteer/browsers-API in den repo-Cache (kein npx-Subprozess mehr)',
      'Server-Bundle deutlich verkleinert (118 KB statt 3 MB) — node_modules werden nicht mehr mitgebündelt, vermeidet "Dynamic require"-Fehler bei CommonJS-Internals',
      'Install-Result zeigt Build-ID, Plattform, Binary-Pfad und Dauer; verifiziert dass das Binary nach Download wirklich existiert',
    ],
  },
  {
    version: '0.3.6',
    date: '2026-05-26',
    notes: [
      'Chrome-Lookup im repo-Cache läuft jetzt rekursiv durch alle Unterordner — egal welche Verzeichnisstruktur Puppeteer beim Download anlegt, Stromo findet das Binary',
      'Edge fliegt aus der automatischen Fallback-Liste (Edge ist mit Puppeteer 24 reproduzierbar instabil)',
      'Wenn doch via PUPPETEER_EXECUTABLE_PATH Edge gesetzt wird: spezielle Launch-Args (pipe:false, eigenes user-data-dir, no-first-run)',
      'install-puppeteer-chrome.mjs verifiziert nach dem Download dass das Binary wirklich existiert; bei manueller Ausführung harter Exit-Code wenn nicht',
    ],
  },
  {
    version: '0.3.5',
    date: '2026-05-26',
    notes: [
      'PDF-Diagnose erkennt Microsoft Edge als verwendeten Browser und warnt: Edge ist mit Puppeteer 24 unzuverlässig (Browser-Launch-Code 1002)',
      'Diagnose-Endpoint liefert zusätzlich repo_cache_chrome_path — die UI kann damit zeigen ob Chrome verfügbar wäre und konkret zur Umstellung anleiten',
      'Empfohlene Lösung bei Edge-Fehlern: PUPPETEER_EXECUTABLE_PATH leeren, dann nimmt Stromo automatisch das Chrome aus dem repo-Cache',
    ],
  },
  {
    version: '0.3.4',
    date: '2026-05-26',
    notes: [
      'Diagnose-Card auf der About-Seite (für Admins): zeigt direkt ob/wo Stromo Chrome gefunden hat, mit Test-PDF-Knopf und Anleitung im Fehlerfall',
      'Neuer Endpoint /api/v1/admin/diagnostics/pdf rendert ein Mini-PDF und liefert strukturierte Fehler-Info zurück',
      'Apple-Meta-Tag um modernes mobile-web-app-capable ergänzt (Deprecation-Warning weg)',
    ],
  },
  {
    version: '0.3.3',
    date: '2026-05-26',
    notes: [
      'PDF-Export robust gemacht: Stromo sucht das Chrome-Binary jetzt selber im repo-lokalen .puppeteer-cache (statt sich auf PUPPETEER_CACHE_DIR-Interpretation zu verlassen) und übergibt Puppeteer den exakten Pfad',
      'Neuer Admin-Endpoint /api/v1/admin/diagnostics/chrome zeigt, wo gesucht wurde und ob ein Browser gefunden wurde — nützlich beim Debuggen ohne Server-Restart',
      'Beim Start loggt der Server jetzt die Quelle des Chrome-Binaries (env / repo-cache / system) und bei Fehlschlag eine konkrete Behebungs-Anleitung',
    ],
  },
  {
    version: '0.3.2',
    date: '2026-05-26',
    notes: [
      'Vorlagen-Editor auf Mobile komplett überarbeitet: Vorlagen-Liste als horizontaler Chip-Scroller oben, Editor full-width darunter',
      'Position-Reihen im Vorlagen-Editor: auf Mobile 2-Zeilen-Layout — Titel-Input bekommt eine eigene Zeile (volle Breite), Pos-Nr und Lösch-Knopf darüber',
      'Inline-Styles im Vorlagen-Editor in CSS-Klassen extrahiert für bessere Mobile-Anpassbarkeit',
    ],
  },
  {
    version: '0.3.1',
    date: '2026-05-26',
    notes: [
      'Detail-Aufgaben-Tabelle auf Desktop schoss in engen Viewports über den Card-Rahmen — jetzt eigener horizontaler Scroll-Container im Card-Inneren',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-05-26',
    notes: [
      'Aufgaben-Detailansicht auf Mobile als Card-Layout statt Tabelle, Tap auf Card öffnet Bearbeitungs-Sheet',
      'Smartphone-Querformat löst dasselbe Mobile-Layout aus wie Hochformat',
      'iOS-Safari zoomt nicht mehr in Input-Felder hinein (font-size auf Mobile ≥ 16px)',
      'Windows-Service-Fix: Puppeteer-Chrome wird per postinstall in einen repo-lokalen Cache geladen, den auch LocalSystem findet',
      'Build-Pipeline-Fix: SQL-Migrations landen jetzt zuverlässig in dist/ und der Server findet sie sowohl als gebündeltes index.js als auch via CLI',
      'PUPPETEER_EXECUTABLE_PATH und Auto-Discovery für system-installiertes Chrome/Edge als Fallback',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-05-26',
    notes: [
      'About-Seite mit Versionsnummer, Changelog, Lizenz-Info (Admin-editierbar)',
      'Mobile-Portrait-Layout (Hamburger-Drawer, Tabellen als Card-Stack, Fullscreen-Modale, Forms 1-spaltig)',
      'Multi-Type-Kontakte: ein Kontakt kann Architekt + Bauherr gleichzeitig sein',
      'Gantt: persistierter Zeitraum (localStorage), +/− zum Erweitern, separate Status-Farben pro Projekt-Status',
      'Cache-Invalidierung: Änderungen im Detail propagieren sofort in den Gantt — kein harter Reload mehr nötig',
      'Klickbare Detail-Termine im Gantt springen direkt zur Aufgabe in der Projektansicht und highlighten sie',
      'Tabellen-Inline-Editing klar erkennbar (Hover-Border + Stift-Icon), Status-Selects mit Farb-Hervorhebung',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-05-26',
    notes: [
      'MVP-Funktionsumfang (Module 1–7): Projekte, Sektionen, Aufgaben, Vorlagen, Kontakte, Benutzer, Audit-Log',
      'PDF-Export Detailblatt und Projektübersicht via Puppeteer',
      'Gantt-View, Detail-Termine, Auslastungsanzeige',
      'E-Mail-Benachrichtigung bei Verzug (täglicher Cron)',
      'PWA-Fähigkeit (Service Worker, Installierbar)',
      'Dokumenten-Upload pro Projekt',
      'Vorlagen-Merge-Mechanismus für Template-Versions-Drift',
    ],
  },
];

export const TECH_STACK: { category: string; items: string[] }[] = [
  {
    category: 'Frontend',
    items: ['React 18', 'Vite 6', 'TypeScript 5', 'TanStack Query', 'Zustand', 'React Router 6', 'dayjs', 'PWA / Workbox'],
  },
  {
    category: 'Backend',
    items: ['Node.js 24 LTS', 'Express 4', 'better-sqlite3 12', 'JWT (jsonwebtoken)', 'bcryptjs', 'Zod', 'Pino'],
  },
  {
    category: 'Datenbank',
    items: ['SQLite 3 (WAL-Mode)', 'Migrations-System (SQL-Files)'],
  },
  {
    category: 'Integrationen',
    items: ['Puppeteer (PDF-Export)', 'Nodemailer + node-cron (Verzugs-Mails)', 'Multer (Dokumenten-Upload)'],
  },
  {
    category: 'Deployment',
    items: ['Windows Server', 'NSSM (Service)', 'PM2-Installer (Alternative)', 'nginx (Reverse Proxy)'],
  },
];

export const PUBLISHER = {
  name: 'SILEO AG',
  url: 'https://sileo.swiss',
  product: 'Stromo Projektverwaltung',
  copyright: `© ${new Date().getFullYear()} SILEO AG`,
};
